import { Anchor, Box, Button, Card, List, Stack, Text } from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';

import { MarketingHero } from '../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../components/marketing/MarketingSection';
import { PublicShell } from '../components/marketing/PublicShell';
import { useCmsImage, useCmsSlot } from '../lib/cms';

export const Route = createFileRoute('/dispute-resolution')({
  component: DisputeResolutionPage,
});

function DisputeResolutionPage() {
  const categories = useCmsSlot<'step_list'>('dispute_resolution.categories');
  const processSteps = useCmsSlot<'step_list'>('dispute_resolution.process_steps');
  const principles = useCmsSlot<'bullet_list'>('dispute_resolution.principles');
  const heroEyebrow = useCmsSlot<'markdown'>('dispute_resolution.hero_eyebrow');
  const heroTitle = useCmsSlot<'markdown'>('dispute_resolution.hero_title');
  const heroLede = useCmsSlot<'markdown'>('dispute_resolution.hero_lede');
  const categoriesEyebrow = useCmsSlot<'markdown'>('dispute_resolution.categories_eyebrow');
  const categoriesTitle = useCmsSlot<'markdown'>('dispute_resolution.categories_title');
  const categoriesLede = useCmsSlot<'markdown'>('dispute_resolution.categories_lede');
  const processEyebrow = useCmsSlot<'markdown'>('dispute_resolution.process_eyebrow');
  const processTitle = useCmsSlot<'markdown'>('dispute_resolution.process_title');
  const processLede = useCmsSlot<'markdown'>('dispute_resolution.process_lede');
  const principlesEyebrow = useCmsSlot<'markdown'>('dispute_resolution.principles_eyebrow');
  const principlesTitle = useCmsSlot<'markdown'>('dispute_resolution.principles_title');
  const ctaEyebrow = useCmsSlot<'markdown'>('dispute_resolution.cta_eyebrow');
  const ctaTitle = useCmsSlot<'markdown'>('dispute_resolution.cta_title');
  const ctaBody = useCmsSlot<'markdown'>('dispute_resolution.cta_body');
  const ctaButton = useCmsSlot<'markdown'>('dispute_resolution.cta_button');
  const ctaContactPrefix = useCmsSlot<'markdown'>('dispute_resolution.cta_contact_prefix');
  const ctaContactEmail = useCmsSlot<'markdown'>('dispute_resolution.cta_contact_email');
  const widerTitle = useCmsSlot<'markdown'>('dispute_resolution.wider_title');
  const widerLede = useCmsSlot<'markdown'>('dispute_resolution.wider_lede');
  const { image: heroImg, ready: heroImgReady } = useCmsImage('dispute_resolution.hero_image');
  return (
    <PublicShell>
      <MarketingHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        lede={heroLede}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.14}
      />

      <MarketingSection
        eyebrow={categoriesEyebrow}
        title={categoriesTitle}
        lede={categoriesLede}
      >
        <Card
          withBorder
          padding="xl"
          radius="lg"
          style={{
            backgroundColor: 'light-dark(rgba(255, 255, 255, 0.95), var(--mantine-color-dark-6))',
            borderColor: 'var(--mantine-color-ink-1)',
          }}
        >
          <List spacing="md" size="md" style={{ lineHeight: 1.6 }}>
            {categories.map((cat, idx) => (
              <List.Item key={idx}>
                <Text fw={600} c="ink.9" component="span">
                  {cat.title}
                </Text>{' '}
                {cat.body}
              </List.Item>
            ))}
          </List>
        </Card>
      </MarketingSection>

      <MarketingSection
        eyebrow={processEyebrow}
        title={processTitle}
        lede={processLede}
        tinted
      >
        <Stack gap="lg">
          {processSteps.map((step, idx) => (
            <DisputeStep key={idx} n={idx + 1} title={step.title} body={step.body} />
          ))}
        </Stack>
      </MarketingSection>

      <MarketingSection eyebrow={principlesEyebrow} title={principlesTitle}>
        <List spacing="md" size="md" style={{ lineHeight: 1.6 }}>
          {principles.map((item, idx) => (
            <List.Item key={idx}>{item}</List.Item>
          ))}
        </List>
      </MarketingSection>

      <MarketingSection eyebrow={ctaEyebrow} title={ctaTitle} tinted>
        <Stack gap="lg">
          <Text size="lg" style={{ lineHeight: 1.6 }}>
            {ctaBody}
          </Text>
          <Box>
            <Button
              component={Link}
              to="/disputes/open"
              size="md"
              variant="gradient"
              gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
            >
              {ctaButton}
            </Button>
          </Box>
          <Text size="sm" c="ink.6" style={{ lineHeight: 1.55 }}>
            {ctaContactPrefix}{' '}
            <Anchor href={`mailto:${ctaContactEmail}`} c="brand.7" fw={500}>
              {ctaContactEmail}
            </Anchor>
            .
          </Text>
        </Stack>
      </MarketingSection>

      <MarketingNextSteps
        title={widerTitle}
        lede={widerLede}
        destinations={['/our-ethos', '/terms', '/faq']}
      />
    </PublicShell>
  );
}

function DisputeStep({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <Card
      withBorder
      padding="lg"
      radius="md"
      style={{
        backgroundColor: 'light-dark(rgba(255, 255, 255, 0.85), var(--mantine-color-dark-6))',
        borderColor: 'var(--mantine-color-ink-1)',
      }}
    >
      <Stack gap="sm">
        <Box style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <Box
            aria-hidden
            style={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background:
                'linear-gradient(135deg, var(--mantine-color-brand-1), var(--mantine-color-sun-1))',
              color: 'var(--mantine-color-brand-7)',
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            {String(n).padStart(2, '0')}
          </Box>
          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="lg" c="ink.9" style={{ lineHeight: 1.35 }}>
              {title}
            </Text>
            <Text c="ink.7" style={{ lineHeight: 1.6 }}>
              {body}
            </Text>
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
}
